import { api } from "@/modules/api";
import { _normalizeFilename } from "./normalizeFilename";
import { uuidModule } from "@/modules/uuid";

export interface UploadFileParams {
  file: File;
  fileId?: string;
}

export interface UploadFileResult {
  fileId: string;
  normalizedFilename: string;
  uploadedFileUrl: string;
}

/**
 * Uploads a file to cloud storage and registers it in the system.
 * This handles the three-step process:
 * 1. Generate a signed URL
 * 2. Upload the file to the signed URL
 * 3. Register the uploaded file
 */
export const _uploadFile = async ({
  file,
  fileId,
}: UploadFileParams): Promise<UploadFileResult> => {
  // Generate file ID if not provided
  const id = fileId ?? uuidModule.generate();

  // Normalize the filename
  const normalizedFilename = _normalizeFilename({ fileName: file.name });

  // Step 1: Generate signed URL
  const signedUrlResponse = await api.post("/v2/file-uploads/generate-signed-url", {
    body: {
      file_id: id,
      file_name: normalizedFilename,
      file_mime_type: file.type || "application/octet-stream",
    },
  });

  if (signedUrlResponse.error || !signedUrlResponse.data) {
    throw new Error("Failed to generate signed URL");
  }

  const { signed_url } = signedUrlResponse.data;

  // Step 2: Upload file to signed URL
  const uploadResponse = await fetch(signed_url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error("Failed to upload file to cloud storage");
  }

  // Extract the base URL without query parameters
  const uploadedFileUrl = signed_url.split("?")[0];

  // Step 3: Register the uploaded file
  const registerResponse = await api.post("/v2/uploaded-files", {
    body: {
      id,
      filename: normalizedFilename,
      mime_type: file.type || "application/octet-stream",
      file_size_bytes: file.size,
      uploaded_file_url: uploadedFileUrl,
    },
  });

  if (registerResponse.error) {
    throw new Error("Failed to register uploaded file");
  }

  return {
    fileId: id,
    normalizedFilename,
    uploadedFileUrl,
  };
};
